import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowLeft,
  ChevronUp,
  Check,
  Layers,
  Plus,
  Sparkles,
  Trash2,
  Users,
  X,
} from 'lucide-react';

import {
  applyDepartmentTemplate,
  createDepartment,
  deleteDepartment,
  listDepartments,
  updateDepartment,
  type Department,
  type IndustryTemplateKey,
} from '@/api/employees.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/hooks/useAuth';

/**
 * Departments admin page. Phase 3 Day 3.
 *
 * Admin-only. Visionary | Integrator can view, create, rename,
 * reorder, delete. Anyone else gets an access-denied card — the
 * rest of the app links here only for admins, but a curious URL
 * paste shouldn't crash.
 *
 * Operations:
 *   - Add department (name + auto-slug)
 *   - Inline rename (click name → edit)
 *   - Reorder via up/down arrows. The server stores `order`; we
 *     swap the order values of two adjacent rows on a click.
 *     Drag-drop reorder is a separate Phase-3-Day-3+ improvement.
 *   - Delete (refuses if employees still reference, with a clear
 *     "go reassign N employees first" message).
 *   - Apply industry template — bulk-add the org's starter set;
 *     idempotent.
 */

const TEMPLATE_OPTIONS: { value: IndustryTemplateKey; label: string }[] = [
  { value: 'custom', label: 'Custom (generic 5-dept starter)' },
  { value: 'manufacturing', label: 'Manufacturing / 3PL / Co-pack' },
  { value: 'saas', label: 'SaaS' },
  { value: 'services', label: 'Services / Consulting' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'agency', label: 'Agency' },
];

export function DepartmentsPage() {
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();

  const canManage = useMemo(() => {
    if (authState.status !== 'authenticated') return false;
    return ['visionary', 'integrator'].includes(authState.me.membership.role);
  }, [authState]);

  const departmentsQuery = useQuery({
    queryKey: ['departments'],
    queryFn: listDepartments,
  });

  const [addOpen, setAddOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Department | null>(null);

  function invalidate(): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: ['departments'] }).then(() => undefined);
  }

  if (!canManage) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Link
          to="/employees"
          className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-4" />
          Back to directory
        </Link>
        <Card className="flex items-start gap-3 p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-warning-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-shark">Admin access required</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              The departments admin page is visible to visionary and integrator roles. Ask one of them to
              invite you with elevated access if you need it.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const departments = departmentsQuery.data ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Link
            to="/employees"
            className="inline-flex items-center gap-1 text-[12px] text-kw-muted hover:text-kw-shark"
          >
            <ArrowLeft className="size-3.5" />
            Back to directory
          </Link>
          <h1 className="typo-h1 mt-1">Departments</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            Configure the per-org list. Used by the directory filter, the accountability chart, and (later)
            RBAC visibility for department leads.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              setApplyOpen(true);
            }}
          >
            <Sparkles className="size-4" />
            Apply template
          </Button>
          <Button
            onClick={() => {
              setAddOpen(true);
            }}
          >
            <Plus className="size-4" />
            Add department
          </Button>
        </div>
      </div>

      {departmentsQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : departmentsQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load departments</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {departmentsQuery.error instanceof Error ? departmentsQuery.error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : departments.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Layers className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No departments yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Add one manually, or apply your industry template to seed a sensible starter set.
            </p>
          </div>
          <Button
            onClick={() => {
              setApplyOpen(true);
            }}
          >
            <Sparkles className="size-4" />
            Apply template
          </Button>
        </Card>
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-kw-border">
            {departments.map((dept, idx) => (
              <DepartmentRow
                key={dept.id}
                dept={dept}
                isFirst={idx === 0}
                isLast={idx === departments.length - 1}
                neighbors={departments}
                editing={editingId === dept.id}
                onEditStart={() => {
                  setEditingId(dept.id);
                }}
                onEditEnd={() => {
                  setEditingId(null);
                }}
                onChanged={() => {
                  void invalidate();
                }}
                onDelete={() => {
                  setPendingDelete(dept);
                }}
              />
            ))}
          </ul>
        </Card>
      )}

      {/* Add modal */}
      <AddDepartmentModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        existingSlugs={new Set(departments.map((d) => d.slug))}
        onCreated={() => {
          void invalidate();
        }}
      />

      {/* Apply template modal */}
      <ApplyTemplateModal
        open={applyOpen}
        onClose={() => {
          setApplyOpen(false);
        }}
        onApplied={() => {
          void invalidate();
        }}
      />

      {/* Delete confirm */}
      <DeleteDepartmentModal
        dept={pendingDelete}
        onClose={() => {
          setPendingDelete(null);
        }}
        onDeleted={() => {
          void invalidate();
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Row
// ─────────────────────────────────────────────────────────────────────────

function DepartmentRow({
  dept,
  isFirst,
  isLast,
  neighbors,
  editing,
  onEditStart,
  onEditEnd,
  onChanged,
  onDelete,
}: {
  dept: Department;
  isFirst: boolean;
  isLast: boolean;
  neighbors: Department[];
  editing: boolean;
  onEditStart: () => void;
  onEditEnd: () => void;
  onChanged: () => void;
  onDelete: () => void;
}) {
  const [draftName, setDraftName] = useState(dept.name);

  const renameMutation = useMutation({
    mutationFn: () => updateDepartment(dept.id, { name: draftName.trim() }),
    onSuccess: () => {
      onChanged();
      onEditEnd();
    },
  });

  // Reorder: swap `order` with the adjacent dept's order. We persist
  // both swaps via two PUT requests in parallel — the order field is
  // independent per-row, so there's no transactional invariant to
  // worry about. Worst case (one succeeds, one fails) you reload and
  // see one dept moved; admin retries.
  const reorderMutation = useMutation({
    mutationFn: async (direction: 'up' | 'down') => {
      const idx = neighbors.findIndex((d) => d.id === dept.id);
      const otherIdx = direction === 'up' ? idx - 1 : idx + 1;
      const other = neighbors[otherIdx];
      if (!other) return;
      await Promise.all([
        updateDepartment(dept.id, { order: other.order }),
        updateDepartment(other.id, { order: dept.order }),
      ]);
    },
    onSuccess: () => {
      onChanged();
    },
  });

  function handleRenameSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!draftName.trim() || draftName.trim() === dept.name) {
      onEditEnd();
      return;
    }
    renameMutation.mutate();
  }

  return (
    <li className="flex items-start gap-3 p-4">
      {/* Reorder controls */}
      <div className="flex shrink-0 flex-col gap-0.5">
        <button
          type="button"
          aria-label={`Move ${dept.name} up`}
          disabled={isFirst || reorderMutation.isPending}
          onClick={() => {
            reorderMutation.mutate('up');
          }}
          className="rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-shark disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ArrowUp className="size-3.5" />
        </button>
        <button
          type="button"
          aria-label={`Move ${dept.name} down`}
          disabled={isLast || reorderMutation.isPending}
          onClick={() => {
            reorderMutation.mutate('down');
          }}
          className="rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-shark disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ArrowDown className="size-3.5" />
        </button>
      </div>

      <div className="min-w-0 flex-1">
        {editing ? (
          <form
            onSubmit={(e) => {
              handleRenameSubmit(e);
            }}
            className="flex items-center gap-2"
          >
            <input
              type="text"
              value={draftName}
              onChange={(e) => {
                setDraftName(e.target.value);
              }}
              autoFocus
              className="min-w-0 flex-1 rounded-kw-md border border-kw-cerulean/40 bg-white px-2 py-1 text-[14px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
            />
            <button
              type="submit"
              aria-label="Save"
              disabled={renameMutation.isPending}
              className="rounded p-1 text-kw-cerulean hover:bg-kw-bg-blue"
            >
              <Check className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Cancel"
              onClick={() => {
                setDraftName(dept.name);
                onEditEnd();
              }}
              className="rounded p-1 text-kw-muted hover:bg-kw-bg"
            >
              <X className="size-4" />
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={onEditStart}
            className="text-left text-[14px] font-medium text-kw-shark hover:underline"
          >
            {dept.name}
          </button>
        )}
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-kw-muted">
          <code className="font-mono text-[11px]">{dept.slug}</code>
          <span className="inline-flex items-center gap-1">
            <Users className="size-3" />
            {String(dept.employeeCount)} {dept.employeeCount === 1 ? 'employee' : 'employees'}
          </span>
          {renameMutation.error ? (
            <span className="text-kw-danger-text">
              Rename failed:{' '}
              {renameMutation.error instanceof Error ? renameMutation.error.message : 'unknown'}
            </span>
          ) : null}
        </div>
      </div>

      {/* Delete — disabled when there are still employees, with a
          tooltip explaining why. */}
      <button
        type="button"
        aria-label={`Delete ${dept.name}`}
        disabled={dept.employeeCount > 0}
        title={
          dept.employeeCount > 0
            ? `Reassign or deactivate the ${String(dept.employeeCount)} employee(s) in ${dept.name} first.`
            : `Delete ${dept.name}`
        }
        onClick={onDelete}
        className="shrink-0 rounded p-1.5 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-kw-muted"
      >
        <Trash2 className="size-4" />
      </button>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Add modal
// ─────────────────────────────────────────────────────────────────────────

function AddDepartmentModal({
  open,
  onClose,
  existingSlugs,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  existingSlugs: Set<string>;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState(''); // optional override

  const mutation = useMutation({
    mutationFn: () =>
      createDepartment({
        name: name.trim(),
        slug: slug.trim() === '' ? undefined : slug.trim(),
      }),
    onSuccess: () => {
      onCreated();
      onClose();
      setName('');
      setSlug('');
    },
  });

  // Live-derived slug preview, mirrors the server's slugifyName().
  const derivedSlug = useMemo(
    () =>
      name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60),
    [name],
  );
  const finalSlug = slug.trim() || derivedSlug;
  const slugClash = finalSlug !== '' && existingSlugs.has(finalSlug);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!name.trim() || slugClash) return;
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Add department" size="sm">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <Input
          label="Name"
          name="name"
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          placeholder="e.g. Marketing"
        />
        <Input
          label="Slug (optional)"
          name="slug"
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value);
          }}
          placeholder={derivedSlug || 'auto-derived from the name'}
          hint="kebab-case, ASCII only. Used in URLs + the API."
        />
        {finalSlug ? (
          <div className="text-[12px]">
            Final slug:{' '}
            <code className="rounded bg-kw-bg px-1 py-0.5 font-mono text-[11px]">{finalSlug}</code>{' '}
            {slugClash ? <span className="text-kw-danger-text">— already in use, pick another</span> : null}
          </div>
        ) : null}
        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {mutation.error instanceof Error ? mutation.error.message : 'Could not add'}
            </p>
          </div>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!name.trim() || slugClash}>
            Add department
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Apply template modal
// ─────────────────────────────────────────────────────────────────────────

function ApplyTemplateModal({
  open,
  onClose,
  onApplied,
}: {
  open: boolean;
  onClose: () => void;
  onApplied: () => void;
}) {
  const [template, setTemplate] = useState<IndustryTemplateKey | ''>('');
  const [result, setResult] = useState<{ added: string[]; skipped: string[] } | null>(null);

  const mutation = useMutation({
    mutationFn: () => applyDepartmentTemplate(template === '' ? undefined : template),
    onSuccess: (data) => {
      setResult({ added: data.addedSlugs, skipped: data.skippedSlugs });
      onApplied();
    },
  });

  function close(): void {
    setResult(null);
    setTemplate('');
    onClose();
  }

  return (
    <Modal open={open} onClose={close} title="Apply industry template" size="sm">
      {result ? (
        <div className="space-y-3">
          <div className="flex items-start gap-3 rounded-kw-md border border-kw-tag-text/30 bg-kw-emerald-light p-3">
            <ChevronUp className="size-5 rotate-180 text-kw-tag-text" />
            <div>
              <p className="text-[14px] font-medium text-kw-tag-text">
                Added {String(result.added.length)} department
                {result.added.length === 1 ? '' : 's'}.
              </p>
              {result.added.length > 0 ? (
                <p className="mt-1 text-[12px] text-kw-tag-text/80">{result.added.join(', ')}</p>
              ) : null}
              {result.skipped.length > 0 ? (
                <p className="mt-2 text-[12px] text-kw-muted">
                  Skipped {String(result.skipped.length)} (already in your org): {result.skipped.join(', ')}.
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={close}>Done</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-[13px] text-kw-muted">
            Bulk-adds the starter departments for your industry. Existing departments matched by slug are left
            alone — safe to run multiple times.
          </p>
          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-medium text-kw-muted">Template</span>
            <select
              value={template}
              onChange={(e) => {
                setTemplate(e.target.value as IndustryTemplateKey | '');
              }}
              className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
            >
              <option value="">Use my org&apos;s industry template</option>
              {TEMPLATE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          {mutation.error ? (
            <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
              <p className="text-[12px] text-kw-danger-text">
                {mutation.error instanceof Error ? mutation.error.message : 'Apply failed'}
              </p>
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                mutation.mutate();
              }}
              isLoading={mutation.isPending}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Delete confirm
// ─────────────────────────────────────────────────────────────────────────

function DeleteDepartmentModal({
  dept,
  onClose,
  onDeleted,
}: {
  dept: Department | null;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const mutation = useMutation({
    mutationFn: () => {
      if (!dept) return Promise.resolve();
      return deleteDepartment(dept.id);
    },
    onSuccess: () => {
      onDeleted();
      onClose();
    },
  });

  return (
    <Modal open={Boolean(dept)} onClose={onClose} title="Delete department" size="sm">
      {dept ? (
        <div className="space-y-4">
          <p className="text-[13px] text-kw-shark">
            Delete <strong>{dept.name}</strong>? This is permanent.
          </p>
          {dept.employeeCount > 0 ? (
            <div className="flex items-start gap-2 rounded-kw-md border border-kw-warning-text/40 bg-kw-warning-bg p-3">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-warning-text" />
              <p className="text-[12px] text-kw-warning-text">
                This department still has <strong>{String(dept.employeeCount)}</strong> employee
                {dept.employeeCount === 1 ? '' : 's'}. Reassign them first.
              </p>
            </div>
          ) : (
            <p className="text-[12px] text-kw-muted">
              No employees are referencing this department, so deletion is safe.
            </p>
          )}
          {mutation.error ? (
            <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
              <p className="text-[12px] text-kw-danger-text">{describeDeleteError(mutation.error)}</p>
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="danger"
              isLoading={mutation.isPending}
              disabled={dept.employeeCount > 0}
              onClick={() => {
                mutation.mutate();
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function describeDeleteError(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: unknown } } }).response;
    const code = response?.data?.error;
    if (code === 'department_in_use') {
      return 'Reassign or deactivate the employees in this department first.';
    }
    if (code === 'department_has_children') {
      return 'Move or delete the sub-departments first.';
    }
  }
  return err instanceof Error ? err.message : 'Could not delete the department';
}
